
import React, { useEffect, useState } from "react";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import { useToast } from "../hooks/useToast";
import { getInvoices } from "../services/api";
import { useNavigate } from "react-router-dom";

interface Invoice {
  id: number;
  vehicle: { id: number; vehicleNo: string; ownerName: string };
  amount: number;
  createdAt: string;
}

const InvoicesImproved: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [searchText, setSearchText] = useState("");
  const { showToast, ToastContainer } = useToast();
  const navigate = useNavigate();

  const fetchInvoices = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getInvoices();
      setInvoices(data);
      showToast("Invoices fetched", "success");
    } catch (err: any) {
      setError(err?.message || "Failed to fetch invoices");
      showToast(err?.message || "Failed to fetch invoices", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line
  }, []);

  const handleSearch = () => {
    // No API call, just filter locally
    // If you want to search via API, replace this logic
    // For now, just setSearchText is enough
  };

  const handleBulkInvoice = () => {
    navigate("/create-bulk-invoice");
  };

  const toggleRow = (id: number) => {
    setExpandedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  // Filter invoices by vehicle number
  const filteredInvoices = invoices.filter((inv) =>
    inv.vehicle.vehicleNo.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 w-full">
      <ToastContainer />
      <Card className="max-w-5xl mx-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <Button color="secondary" onClick={() => navigate(-1)}>
            Back
          </Button>
          <h1 className="text-2xl font-bold text-blue-700">Invoices</h1>
          <Button color="primary" onClick={handleBulkInvoice}>
            Create Bulk Invoice
          </Button>
        </div>
        <div className="flex flex-col md:flex-row md:items-center gap-2 mb-4">
          <input
            type="text"
            placeholder="Search by vehicle number"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="border px-3 py-2 rounded w-full md:w-64"
          />
          <Button color="primary" onClick={handleSearch}>
            Search
          </Button>
          <Button onClick={fetchInvoices} color="primary">
            Refresh
          </Button>
        </div>
        {loading && <LoadingSpinner />}
        {error && <div className="text-red-600 mb-2">{error}</div>}
        {/* Desktop Table */}
        <div className="hidden md:block w-full bg-white shadow rounded-lg overflow-x-auto mt-4">
          <table className="w-full border-collapse border">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-2 text-left">Vehicle</th>
                <th className="p-2 text-left">Amount</th>
                <th className="p-2 text-left">Date</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.map((inv) => (
                <tr
                  key={inv.id}
                  className="border-b cursor-pointer hover:bg-gray-50 transition"
                  onClick={() => navigate(`/create-invoice/${inv.vehicle.id}`)}
                >
                  <td className="p-2">{inv.vehicle.vehicleNo}</td>
                  <td className="p-2">₹{inv.amount}</td>
                  <td className="p-2">{new Date(inv.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
              {filteredInvoices.length === 0 && (
                <tr>
                  <td colSpan={3} className="text-center py-4 text-gray-500">
                    No invoices found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Mobile Collapsible Cards */}
        <div className="md:hidden flex flex-col gap-3 w-full mt-4">
          {filteredInvoices.map((inv) => {
            const isExpanded = expandedRows.includes(inv.id);
            return (
              <div
                key={inv.id}
                className="bg-white shadow rounded-lg p-4 w-full cursor-pointer"
              >
                <div
                  className="flex justify-between items-center"
                  onClick={() => toggleRow(inv.id)}
                >
                  <div>
                    <p className="font-semibold text-gray-800">
                      {inv.vehicle.vehicleNo}
                    </p>
                    <p className="text-gray-600">₹{inv.amount}</p>
                  </div>
                  <div className="text-gray-500">
                    {new Date(inv.createdAt).toLocaleDateString()} {" "}
                    <span className="ml-2">{isExpanded ? "▲" : "▼"}</span>
                  </div>
                </div>
                {isExpanded && (
                  <div
                    className="mt-3 border-t pt-2 text-gray-700"
                    onClick={() => navigate(`/create-invoice/${inv.vehicle.id}`)}
                  >
                    <p>
                      <strong>Vehicle:</strong> {inv.vehicle.vehicleNo}
                    </p>
                    <p>
                      <strong>Owner:</strong> {inv.vehicle.ownerName}
                    </p>
                    <p>
                      <strong>Amount:</strong> ₹{inv.amount}
                    </p>
                    <p>
                      <strong>Date:</strong> {new Date(inv.createdAt).toLocaleDateString()}
                    </p>
                    <p className="mt-2 text-sm text-gray-500">
                      Tap anywhere in this card to create invoice
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
};

export default InvoicesImproved;
